/**
 * Navigation sections — the single source for the desktop sidebar and the
 * mobile drawer. Items hide unless the user holds one of `permission`.
 */

import type { ElementType } from 'react';
import {
  BarChart3,
  Banknote,
  Bell,
  BookOpen,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  KanbanSquare,
  Building2,
  Landmark,
  FileText,
  FolderKanban,
  Key,
  LayoutDashboard,
  Shield,
  ShoppingBag,
  Sun,
  UserRound,
  Receipt,
  Coins,
  ScanText,
  Users,
  Wallet,
} from 'lucide-react';

export interface NavItem {
  /** i18n key in the `nav` namespace, e.g. `items.dashboard`. */
  labelKey: string;
  href: string;
  icon: ElementType;
  /** Show if the user has any of these permissions. */
  permission?: string | string[];
  role?: string;
}

export interface NavSection {
  key: string;
  /** Heading i18n key (omit for the untitled top section). */
  labelKey?: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'main',
    items: [
      { labelKey: 'items.today', href: '/today', icon: Sun, permission: 'projects.view' },
      { labelKey: 'items.dashboard', href: '/dashboard', icon: LayoutDashboard },
      { labelKey: 'items.alerts', href: '/alerts', icon: Bell, permission: 'alerts.view' },
    ],
  },
  {
    key: 'studio',
    labelKey: 'sections.studio',
    // The order follows the business flow: client → project → proposal → the work.
    items: [
      { labelKey: 'items.clients', href: '/clients', icon: UserRound, permission: 'clients.view' },
      { labelKey: 'items.projects', href: '/projects', icon: FolderKanban, permission: 'projects.view' },
      { labelKey: 'items.proposals', href: '/proposals', icon: ClipboardList, permission: 'proposals.view' },
      { labelKey: 'items.board', href: '/projects/board', icon: KanbanSquare, permission: 'projects.view' },
      { labelKey: 'items.tasks', href: '/tasks', icon: CheckSquare, permission: 'projects.view' },
      { labelKey: 'items.calendar', href: '/calendar', icon: CalendarDays, permission: 'projects.view' },
    ],
  },
  {
    key: 'finance',
    labelKey: 'sections.finance',
    items: [
      { labelKey: 'items.invoices', href: '/invoices', icon: Receipt, permission: 'invoices.view' },
      { labelKey: 'items.payments', href: '/payments', icon: Banknote, permission: 'payments.view' },
      { labelKey: 'items.bank', href: '/banking', icon: Landmark, permission: 'banking.view' },
      { labelKey: 'items.receipts', href: '/purchases/receipts', icon: ScanText, permission: 'purchases.view' },
      { labelKey: 'items.purchases', href: '/purchases', icon: ShoppingBag, permission: 'purchases.view' },
      { labelKey: 'items.expenses', href: '/expenses', icon: Coins, permission: 'expenses.view' },
      { labelKey: 'items.owner', href: '/owner', icon: Wallet, permission: 'owner.view' },
      { labelKey: 'items.reports', href: '/reports', icon: BarChart3, permission: 'reports.view' },
    ],
  },
  {
    key: 'admin',
    labelKey: 'sections.admin',
    items: [
      { labelKey: 'items.studioSettings', href: '/settings/studio', icon: Building2, permission: 'business.settings' },
      { labelKey: 'items.users', href: '/admin/users', icon: Users, permission: 'users.view' },
      { labelKey: 'items.roles', href: '/admin/roles', icon: Shield, permission: 'roles.view' },
      { labelKey: 'items.apiKeys', href: '/admin/api-keys', icon: Key, permission: 'admin.api_keys' },
      { labelKey: 'items.auditLogs', href: '/admin/audit-logs', icon: FileText, permission: 'audit.view' },
      { labelKey: 'items.documentation', href: '/docs', icon: BookOpen, role: 'admin' },
    ],
  },
];
